import { homeKeys } from '@/features/home/queries/query-keys';
import { playerSessionKeys } from '@/features/player-session/queries/query-keys';
import { PlayerSessionServices } from '@/features/player-session/services';
import { playerKeys } from '@/features/player/queries/query-keys';
import { ApiError, PaginatedResponse } from '@/libs/api/types';
import { queryClient } from '@/libs/tanstack-query/query-client';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';

interface SessionPaginatedResponse<T = unknown> extends PaginatedResponse<T> {
  meta: PaginatedResponse<T>['meta'] & {
    totalSessionRoster: number | null;
    totalNeedsClubs: number | null;
    totalPinsArchived: number | null;
  };
}
export const useRemoveCoachFromSessionQuery = (): UseMutationResult<
  void,
  ApiError,
  playerSession.EndSessionRequest
> => {
  return useMutation<void, ApiError, playerSession.EndSessionRequest>({
    mutationFn: request => PlayerSessionServices.removeCoachFromSession(request),
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({ queryKey: playerSessionKeys.all });
      queryClient.invalidateQueries({ queryKey: homeKeys.coach(request.coachId) });
    },
  });
};

export const useListPlayersSessionQuery = (
  request: playerSession.SortFilterSearchSessionsRequest,
  featureKey?: string,
): UseQueryResult<SessionPaginatedResponse<playerSession.SessionLookupData>, ApiError> => {
  return useQuery<
    playerSession.SortFilterSearchSessionsResult,
    ApiError,
    SessionPaginatedResponse<playerSession.SessionLookupData>
  >({
    queryKey: playerSessionKeys.list(featureKey ?? playerSessionKeys.sessionList)(request),
    queryFn: () => PlayerSessionServices.sortFilterSearchSessions(request),
    enabled: !!request.sessionId,
    select: data => {
      const paginated = request.pagination === true;
      const pageSize = request.resultsPerPage ?? 20;
      const totalFromFirst = data?.sessions?.[0]?.totalQueryResults ?? 0;
      const total = paginated
        ? totalFromFirst
        : totalFromFirst > 0
          ? totalFromFirst
          : (data?.sessions?.length ?? 0);
      const numberOfPages = paginated ? Math.max(1, Math.ceil(totalFromFirst / pageSize)) : 1;
      // const _data = (data?.sessions ?? []).map(item => ({
      //   ...item,
      //   pins: MOCK_PINS,
      //   attempts: MOCK_ATTEMPTS,
      // }));
      return {
        data: data?.sessions ?? [],
        meta: {
          page: request.expectedPage ?? 1,
          limit: paginated ? pageSize : total,
          total,
          totalPages: numberOfPages,
          totalSessionRoster: data?.totalSessionRoster ?? 0,
          totalNeedsClubs: data?.totalNeedsClubs ?? 0,
          totalPinsArchived: data?.totalPinsArchived ?? 0,
        },
      };
    },
    placeholderData: previousData => previousData,
  });
};

export function useUpdatePlayerProgressMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: playerSession.UpdatePlayerProgressRequest) =>
      PlayerSessionServices.updatePlayerProgress(request),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: playerKeys.profile(variables.playerId) });
    },
  });
}

export function useCheckSessionForPlayerProgressMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: playerSession.CheckSessionForPlayerProgressRequest) =>
      PlayerSessionServices.checkSessionForPlayerProgress(request),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: playerSessionKeys.checkSessionForPlayerProgress(variables),
      });
    },
  });
}
